import { createContext, useContext, useEffect, useState } from 'react';
import { type ChargeDomain, LegType, useCalculateSubCharge } from 'src/use-cases/invoice/useQuestionnaire';
import { ContractChargeDetailDto, ResponseDtoSetChargeDomain } from 'src/generated/services/TFinancialApi';
import { useSelectedDeviation } from '../hooks/useSelectedDeviation';
import { useShipmentSelector } from '../../../ShipmentSelector';

export type TNewChargeForm = {
  legType?: LegType;
  chargeDomain?: ChargeDomain;
};

type TNewChargeContext = {
  newChargeState: TNewChargeForm;
  setNewChargeState: React.Dispatch<React.SetStateAction<TNewChargeForm>>;
  subChargeDomains: ContractChargeDetailDto[];
  isDeviationEditing: boolean;
  setIsDeviationEditing: React.Dispatch<React.SetStateAction<boolean>>;
  errors: Record<string, string>;
  setErrors: (errors: Record<string, string>) => void;
};

export type TChargeDomainKey = ResponseDtoSetChargeDomain['data'];
// eslint-disable-next-line react-refresh/only-export-components
export const CHARGE_DOMAIN: TChargeDomainKey = [
  'COLLECTION',
  'CONTAINER_CLEARANCE',
  'CONTAINER_MAINTENANCE',
  'CUSTOMS_CLEARANCE',
  'DEFAULT_CHARGE_DOMAIN',
  'DELIVERY',
  'DOCUMENTATION',
  'FREIGHT',
  'FUEL_SURCHARGE',
  'HANDLING_CHARGE',
  'INSPECTION',
  'OVERTIME',
  'PICK_UP',
  'PICK_UP_AND_DROP_OFF',
  'PORT_CHARGES',
  'SEAL_FEE',
  'SPECIAL_HANDLING',
  'STORAGE',
  'THC',
  'TOTAL_ORIGIN_CHARGES',
  'VGM_FEE',
  'SCREE',
];

export const NewChargeContext = createContext<TNewChargeContext | null>(null);

export const UCTNewChargeProvider = ({ children }) => {
  const { selectedDeviation } = useSelectedDeviation();
  const { shipment } = useShipmentSelector();

  const [newChargeState, setNewChargeState] = useState<TNewChargeForm>({});

  // TODO: handing error this way is not elegant, need to use yup and react-hook-form
  const [errors, setErrors] = useState<Record<string, string>>({
    legType: null,
    chargeDomain: null,
  });

  // Check if this is NRCF: contractId === null OR shippingServiceId === null
  const isNrcf = !shipment?.contractId || !shipment?.shippingServiceId;

  const { data: subChargeDomains } = useCalculateSubCharge(selectedDeviation.id, newChargeState.legType, newChargeState.chargeDomain, isNrcf);

  const [isDeviationEditing, setIsDeviationEditing] = useState(true);

  useEffect(() => {
    if (selectedDeviation?.status === 'DONE' || selectedDeviation?.status === 'REJECTED_DONE') {
      setIsDeviationEditing(false);
    } else {
      setIsDeviationEditing(true);
    }
  }, [selectedDeviation]);

  return (
    <NewChargeContext.Provider
      value={{
        newChargeState,
        setNewChargeState,
        subChargeDomains,
        isDeviationEditing,
        setIsDeviationEditing,
        errors,
        setErrors,
      }}
    >
      {children}
    </NewChargeContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useNewChargeContext = () => {
  const ctx = useContext(NewChargeContext);
  if (ctx) return ctx;
  throw new Error('useNewChargeContext in NewChargeContext please, only for UCT');
};
